import {
  type MediaAsset,
  createLegacyMediaAssets,
  normalizeMediaAssets,
} from '@/ai/media/domain/media-asset';

export const AI_JOB_CONTRACT_VERSION = 1 as const;

export const AI_JOB_ERROR_CODES = {
  CONTENT_POLICY_BLOCKED: 'CONTENT_POLICY_BLOCKED',
  PROVIDER_CREATE_FAILED: 'PROVIDER_CREATE_FAILED',
  PROVIDER_FAILED: 'PROVIDER_FAILED',
  QUEUE_ENQUEUE_FAILED: 'QUEUE_ENQUEUE_FAILED',
  QUEUE_DISPATCH_EXHAUSTED: 'QUEUE_DISPATCH_EXHAUSTED',
  AI_JOB_CONCURRENCY_LIMIT_REACHED: 'AI_JOB_CONCURRENCY_LIMIT_REACHED',
  CLIENT_REQUEST_ID_CONFLICT: 'CLIENT_REQUEST_ID_CONFLICT',
  WORKSPACE_TOOL_VERSION_CHANGED: 'WORKSPACE_TOOL_VERSION_CHANGED',
  OUTPUT_EMPTY: 'OUTPUT_EMPTY',
  TIMEOUT: 'TIMEOUT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

export type AiJobInputPayload = {
  v: typeof AI_JOB_CONTRACT_VERSION;
  core: {
    inputImageUrl?: string;
  };
  /** Tool-normalized params stored for debugging and replay. */
  params: unknown;
};

export type AiJobOutputPayload = {
  v: typeof AI_JOB_CONTRACT_VERSION;
  /** Legacy compatibility arrays. New presentation code should consume assets. */
  images: string[];
  /** Optional on persisted v1 rows created before video outputs existed. */
  videos?: string[];
  /** Canonical output model for both image and video results. */
  assets?: MediaAsset[];
  meta?: Record<string, unknown>;
};

export type NormalizedAiJobOutputPayload = Omit<
  AiJobOutputPayload,
  'videos' | 'assets'
> & {
  videos: string[];
  assets: MediaAsset[];
};

export type AiJobAdminV2OutputSlots = Record<string, unknown>;

export type AiJobErrorPayload = {
  v: typeof AI_JOB_CONTRACT_VERSION;
  code: string;
  message: string;
  details?: unknown;
  provider?: string;
  providerStatus?: string;
  providerError?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeOutputSlotMap(
  value: unknown
): AiJobAdminV2OutputSlots | null {
  if (!isRecord(value) || Array.isArray(value)) {
    return null;
  }

  return Object.fromEntries(Object.entries(value));
}

function extractCoreInput(params: unknown): AiJobInputPayload['core'] {
  if (!isRecord(params)) return {};

  const inputImageUrl = params.inputImageUrl;
  if (typeof inputImageUrl === 'string') {
    return { inputImageUrl };
  }

  return {};
}

export function makeAiJobInputPayload(params: unknown): AiJobInputPayload {
  return {
    v: AI_JOB_CONTRACT_VERSION,
    core: extractCoreInput(params),
    params,
  };
}

export function normalizeAiJobInput(value: unknown): AiJobInputPayload {
  if (isRecord(value) && value.v === AI_JOB_CONTRACT_VERSION) {
    const core = isRecord(value.core) ? value.core : {};
    return {
      v: AI_JOB_CONTRACT_VERSION,
      core: {
        inputImageUrl:
          typeof core.inputImageUrl === 'string'
            ? core.inputImageUrl
            : undefined,
      },
      params: 'params' in value ? value.params : undefined,
    };
  }

  // Historical rows may store raw provider params directly.
  return makeAiJobInputPayload(value);
}

export function makeAiJobOutputPayload(
  images: string[],
  meta?: Record<string, unknown>,
  videos: string[] = [],
  assets?: MediaAsset[]
): AiJobOutputPayload {
  return {
    v: AI_JOB_CONTRACT_VERSION,
    images,
    videos,
    assets: assets ?? createLegacyMediaAssets({ images, videos }),
    ...(meta ? { meta } : {}),
  };
}

export function normalizeAiJobOutput(
  value: unknown
): NormalizedAiJobOutputPayload | null {
  if (!isRecord(value)) return null;

  if (value.v === AI_JOB_CONTRACT_VERSION) {
    const images = Array.isArray(value.images)
      ? value.images.filter((x) => typeof x === 'string')
      : [];

    const videos = Array.isArray(value.videos)
      ? value.videos.filter((x) => typeof x === 'string')
      : [];
    const assets = normalizeMediaAssets(value.assets);
    const meta = isRecord(value.meta) ? value.meta : undefined;

    return {
      v: AI_JOB_CONTRACT_VERSION,
      images,
      videos,
      assets:
        assets.length > 0
          ? assets
          : createLegacyMediaAssets({ images, videos }),
      ...(meta ? { meta } : {}),
    };
  }

  // Historical rows may store { images: string[] }.
  if (
    Array.isArray(value.images) &&
    value.images.every((x) => typeof x === 'string')
  ) {
    const images = value.images as string[];
    return {
      v: AI_JOB_CONTRACT_VERSION,
      images,
      videos: [],
      assets: createLegacyMediaAssets({ images, videos: [] }),
    };
  }

  return null;
}

export function makeAiJobAdminV2OutputMeta(params: {
  outputSlots: AiJobAdminV2OutputSlots;
}) {
  return {
    adminV2: {
      outputSlots: params.outputSlots,
    },
  };
}

export function readAiJobAdminV2OutputSlots(
  output: AiJobOutputPayload | null
): AiJobAdminV2OutputSlots | null {
  const adminV2 = output?.meta?.adminV2;
  if (!isRecord(adminV2)) {
    return null;
  }

  return normalizeOutputSlotMap(adminV2.outputSlots);
}

export function mergeAiJobAdminV2OutputSlotsMeta(params: {
  meta?: Record<string, unknown>;
  outputSlots: AiJobAdminV2OutputSlots;
}) {
  const currentAdminV2 = isRecord(params.meta?.adminV2)
    ? params.meta.adminV2
    : {};

  return {
    ...(params.meta ?? {}),
    adminV2: {
      ...currentAdminV2,
      outputSlots: params.outputSlots,
    },
  };
}

export function makeAiJobErrorPayload({
  code,
  message,
  details,
  provider,
  providerStatus,
  providerError,
}: {
  code: string;
  message: string;
  details?: unknown;
  provider?: string;
  providerStatus?: string;
  providerError?: unknown;
}): AiJobErrorPayload {
  return {
    v: AI_JOB_CONTRACT_VERSION,
    code,
    message,
    ...(details !== undefined ? { details } : {}),
    ...(provider ? { provider } : {}),
    ...(providerStatus ? { providerStatus } : {}),
    ...(providerError !== undefined ? { providerError } : {}),
  };
}

export function normalizeAiJobError(value: unknown): AiJobErrorPayload | null {
  if (!isRecord(value)) return null;

  if (value.v === AI_JOB_CONTRACT_VERSION) {
    const code = typeof value.code === 'string' ? value.code : 'UNKNOWN';
    const message = typeof value.message === 'string' ? value.message : 'Error';

    return {
      v: AI_JOB_CONTRACT_VERSION,
      code,
      message,
      ...(value.details !== undefined ? { details: value.details } : {}),
      ...(typeof value.provider === 'string'
        ? { provider: value.provider }
        : {}),
      ...(typeof value.providerStatus === 'string'
        ? { providerStatus: value.providerStatus }
        : {}),
      ...(value.providerError !== undefined
        ? { providerError: value.providerError }
        : {}),
    };
  }

  // Historical error rows are normalized with conservative heuristics.
  const providerStatus =
    typeof value.providerStatus === 'string' ? value.providerStatus : undefined;
  const providerError =
    'providerError' in value ? value.providerError : undefined;

  const message =
    typeof value.message === 'string'
      ? value.message
      : providerStatus
        ? `Provider failed (${providerStatus})`
        : 'Error';

  return makeAiJobErrorPayload({
    code: providerStatus ? AI_JOB_ERROR_CODES.PROVIDER_FAILED : 'UNKNOWN',
    message,
    details: value,
    providerStatus,
    providerError,
  });
}
